import { useState, useEffect } from 'react';
import { X, Plus, Trash2, Edit2, AlertTriangle, Braces, Check } from 'lucide-react';
import { userTypeApi } from '../../utils/api';
import { DATA_TYPES } from '../../utils/constants';
import toast from 'react-hot-toast';

const ALL_BASE_TYPES = [
  ...new Set([
    ...DATA_TYPES.RELATIONAL,
    ...DATA_TYPES.NOSQL,
    ...DATA_TYPES.ANALYTICAL,
  ]),
].sort();

const EMPTY_FORM = {
  name: '',
  baseType: DATA_TYPES.RELATIONAL[0],
  size: '',
  fillRule: '',
  validationRule: '',
};

const UserTypesModal = ({ onClose, onTypesChanged }) => {
  const [types, setTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState(null); // null = creating new
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState(null); // { udt, usedInModels }
  const [loadingDelete, setLoadingDelete] = useState(false);

  // Rename confirmation state (when name changes & models are affected)
  const [renameConfirm, setRenameConfirm] = useState(null); // { payload, modelsAffected }

  useEffect(() => { load(); }, []);

  const load = async () => {
    setIsLoading(true);
    try {
      const data = await userTypeApi.getAll();
      setTypes(data);
      onTypesChanged?.(data);
    } catch {
      toast.error('Failed to load user-defined types');
    } finally {
      setIsLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (udt) => {
    setEditingId(udt._id);
    setForm({
      name: udt.name,
      baseType: udt.baseType,
      size: udt.size ?? '',
      fillRule: udt.fillRule || '',
      validationRule: udt.validationRule || '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    if (!form.baseType) return toast.error('Base type is required');

    const payload = {
      name: form.name.trim(),
      baseType: form.baseType,
      size: form.size !== '' ? parseInt(form.size, 10) : null,
      fillRule: form.fillRule,
      validationRule: form.validationRule,
    };

    setSaving(true);
    try {
      if (editingId) {
        const res = await userTypeApi.update(editingId, payload);
        // If name changed and models are affected, ask for confirmation
        if (res.nameChanged && res.modelsAffected?.length > 0) {
          setRenameConfirm({ result: res, modelsAffected: res.modelsAffected });
        } else {
          toast.success('Type updated');
        }
        await load();
      } else {
        await userTypeApi.create(payload);
        toast.success(`UDT "${payload.name}" created`);
        await load();
      }
      setShowForm(false);
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const requestDelete = async (udt) => {
    setLoadingDelete(true);
    try {
      const { usedInModels } = await userTypeApi.getUsage(udt._id);
      setDeleteTarget({ udt, usedInModels });
    } catch {
      toast.error('Could not check type usage');
    } finally {
      setLoadingDelete(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await userTypeApi.delete(deleteTarget.udt._id);
      toast.success(`Type "${deleteTarget.udt.name}" deleted`);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(err.message || 'Failed to delete');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Braces size={20} className="text-purple-500" />
              User-Defined Types
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Custom types shared across all your models
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-700 dark:text-gray-200">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">

          {/* Creation / Edition form */}
          {showForm && (
            <div className="border-2 border-purple-300 dark:border-purple-600 rounded-lg p-4 bg-purple-50 dark:bg-purple-900/20">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
                {editingId ? 'Edit Type' : 'New User-Defined Type'}
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {/* Name */}
                <div className="col-span-2 md:col-span-1">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Type Name *
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. CustomerID, EmailAddress"
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Will appear as <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">UDT:{form.name || '…'}</code> in field lists
                  </p>
                </div>

                {/* Base Type */}
                <div className="col-span-2 md:col-span-1">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Base Logical Type *
                  </label>
                  <select
                    value={form.baseType}
                    onChange={e => setForm(f => ({ ...f, baseType: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {ALL_BASE_TYPES.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>

                {/* Size */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Size (optional)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={form.size}
                    onChange={e => setForm(f => ({ ...f, size: e.target.value }))}
                    placeholder="e.g. 255"
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Fill Rule */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Fill Rule (optional)
                  </label>
                  <input
                    type="text"
                    value={form.fillRule}
                    onChange={e => setForm(f => ({ ...f, fillRule: e.target.value }))}
                    placeholder="e.g. Required, numeric only"
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Validation Rule */}
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Validation Rule (optional)
                  </label>
                  <textarea
                    rows={2}
                    value={form.validationRule}
                    onChange={e => setForm(f => ({ ...f, validationRule: e.target.value }))}
                    placeholder="e.g. Must match pattern [A-Z]{2}-[0-9]{6}"
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                  />
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm flex items-center gap-2 disabled:opacity-50"
                >
                  <Check size={14} />
                  {saving ? 'Saving…' : (editingId ? 'Update' : 'Create')}
                </button>
                <button
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Add button (shown when form is hidden) */}
          {!showForm && (
            <button
              onClick={openCreate}
              className="w-full py-3 border-2 border-dashed border-purple-300 dark:border-purple-600 text-purple-600 dark:text-purple-400 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
            >
              <Plus size={16} />
              New User-Defined Type
            </button>
          )}

          {/* List */}
          {isLoading ? (
            <div className="text-center py-10 text-gray-500 dark:text-gray-400">Loading…</div>
          ) : types.length === 0 ? (
            <div className="text-center py-10 text-gray-500 dark:text-gray-400">
              <Braces size={40} className="mx-auto mb-3 opacity-30" />
              <p>No user-defined types yet.</p>
              <p className="text-sm mt-1">Create your first type above.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {types.map(udt => (
                <div
                  key={udt._id}
                  className="flex items-start gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-purple-400 dark:hover:border-purple-500 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {udt.name}
                      </span>
                      <span className="px-2 py-0.5 rounded text-xs font-mono bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                        UDT:{udt.name}
                      </span>
                      <span className="px-2 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                        {udt.baseType}{udt.size ? `(${udt.size})` : ''}
                      </span>
                    </div>
                    {(udt.fillRule || udt.validationRule) && (
                      <div className="mt-1.5 space-y-0.5">
                        {udt.fillRule && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            <span className="font-medium">Fill:</span> {udt.fillRule}
                          </p>
                        )}
                        {udt.validationRule && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            <span className="font-medium">Validation:</span> {udt.validationRule}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      onClick={() => openEdit(udt)}
                      className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors"
                      title="Edit"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => requestDelete(udt)}
                      disabled={loadingDelete}
                      className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors disabled:opacity-40"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
          >
            Close
          </button>
        </div>
      </div>

      {/* Rename confirmation (name changed, models affected) */}
      {renameConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle size={22} className="text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Type Renamed</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  The type was renamed and is used in{' '}
                  <strong>{renameConfirm.modelsAffected.length}</strong> model(s). All references have been updated automatically:
                </p>
                <ul className="mt-2 space-y-1">
                  {renameConfirm.modelsAffected.map(m => (
                    <li key={m._id} className="text-sm text-gray-700 dark:text-gray-300">• {m.name}</li>
                  ))}
                </ul>
              </div>
            </div>
            <button
              onClick={() => setRenameConfirm(null)}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle size={22} className="text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Delete User-Defined Type</h3>
                {deleteTarget.usedInModels.length > 0 ? (
                  <>
                    <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                      This type is used in <strong>{deleteTarget.usedInModels.length}</strong> model(s) and cannot be deleted until all references are removed:
                    </p>
                    <ul className="mt-2 space-y-1">
                      {deleteTarget.usedInModels.map(m => (
                        <li key={m._id} className="text-sm text-gray-700 dark:text-gray-300">• {m.name}</li>
                      ))}
                    </ul>
                    <button
                      onClick={() => setDeleteTarget(null)}
                      className="mt-4 w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
                    >
                      Close
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Are you sure you want to delete <strong>"{deleteTarget.udt.name}"</strong>? This action cannot be undone.
                    </p>
                    <div className="flex gap-3 mt-4">
                      <button
                        onClick={() => setDeleteTarget(null)}
                        className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={confirmDelete}
                        className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserTypesModal;
